// NPM Imports
import React, { useContext, useEffect, useState } from 'react'
// Quote Context
import { useQuoteContext } from '../../hooks/useQuoteContext'
// Component Imports
// JS Imports
import {
   //@ts-ignore
   formatFloatAsCurrencySymbol,
   //@ts-ignore
   removeDiscountNameVariables,
} from '../../common/helpers.mjs'
import { shouldDisableDiscount } from '../../js/validate'
import DiscountDescriptionBox from './DiscountDescriptionBox'
// Asset Imports

interface NumberDiscountProps {
   discount_key: string
   children?: React.ReactNode
   order?: number
}

export default function NumberDiscount({
   discount_key,
   children,
   order = 99,
}: NumberDiscountProps) {
   const state = useQuoteContext()

   const maxDecimals =
      state.discount_list[discount_key]['DiscountType'] === 'percent' ? 5 : 2

   const storedNumericValue: number =
      state.promotionalDiscounts?.['numberDiscountValues']?.[discount_key] ?? 0

   // Local string state preserves trailing zeros while the user is typing
   const [displayValue, setDisplayValue] = useState<string>(
      storedNumericValue > 0 ? storedNumericValue.toString() : '',
   )

   // Sync display value when global state changes externally (e.g. promotionalDiscounts loaded)
   useEffect(() => {
      setDisplayValue(
         storedNumericValue > 0 ? storedNumericValue.toString() : '',
      )
   }, [storedNumericValue])

   const disable_discount = shouldDisableDiscount(state, discount_key) //&& !(state.promotionalDiscounts[discount_key] != undefined && state.promotionalDiscounts[discount_key]);
   // A new name for the discount that replaces $PRODUCTNAME with the actual product name if it applies to only one product
   let formattedDiscountName = ''
   if (
      state.discount_list?.[discount_key]?.['DiscountName']?.includes(
         '$PRODUCTNAME',
      )
   ) {
      if (
         state.discount_list?.[discount_key]?.['ApplyTo']?.split(',')?.length ==
         1
      )
         formattedDiscountName = state.discount_list?.[discount_key]?.[
            'DiscountName'
         ]?.replace(
            /\$PRODUCTNAME/g,
            state.product_list?.[
               state.discount_list?.[discount_key]?.['ApplyTo']
            ]?.['ProductName'],
         )
      else
         formattedDiscountName = removeDiscountNameVariables(
            state.discount_list?.[discount_key]?.['DiscountName'],
         )
   } else
      formattedDiscountName =
         state.discount_list?.[discount_key]?.['DiscountName']

   return (
      <div className='row' style={{ order: order }}>
         <div className='col-bhr1'>
            <DiscountDescriptionBox
               discountDescription={
                  state.discount_list[discount_key]['DiscountDescription']
               }
               discountName={
                  <>
                     {formattedDiscountName}
                     {state.discount_list[discount_key]['DiscountType'] !=
                     'percent'
                        ? ' ' + state.currencyObject.Symbol + ' '
                        : ''}
                  </>
               }
            />
         </div>

         <div className='col'></div>

         <div className='col'>
            {/* <input className="form-control" type="number" step="0.01" value={state.productPaidServiceAmount} onChange={e => state.setProductPaidServiceAmount(e.target.value)} onBlur={() => state.setProductPaidServiceAmount(state.productPaidServiceAmount < 0.01 ? 0 : Math.floor(state.productPaidServiceAmount*100)/100)}></input> */}

            <input
               className='form-control'
               type='text'
               inputMode='decimal'
               disabled={disable_discount}
               value={displayValue}
               placeholder={
                  state.discount_list[discount_key]['DiscountType'] ==
                     'fixed' && state.currencyObject.Code != undefined
                     ? state.currencyObject.Code
                     : '0'
               }
               onChange={(e) => {
                  const raw = e.target.value

                  // Only allow digits, one leading minus (not applicable here), and one decimal point
                  if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return

                  // Enforce max decimal places while typing
                  const dotIndex = raw.indexOf('.')
                  if (
                     dotIndex !== -1 &&
                     raw.length - dotIndex - 1 > maxDecimals
                  )
                     return

                  // Enforce max value for percent discounts while typing — clamp to max
                  const parsedValue = parseFloat(raw)
                  let numericValue = parsedValue
                  let clampedRaw = raw
                  if (
                     state.discount_list[discount_key]['DiscountType'] ===
                        'percent' &&
                     !isNaN(parsedValue)
                  ) {
                     const maxValue = state.discount_list[discount_key][
                        'MaxDiscount'
                     ]
                        ? state.discount_list[discount_key]['MaxDiscount']
                        : 100
                     if (parsedValue > maxValue) {
                        numericValue = maxValue
                        clampedRaw = String(maxValue)
                     }
                  }

                  // Update local display string (preserves trailing zeros and bare decimal point)
                  setDisplayValue(clampedRaw)

                  // Commit numeric value to global state
                  const committedValue =
                     isNaN(numericValue) || raw === '' ? 0 : numericValue
                  // Toggles on discount if above 0. If 0, discount is off.
                  state.setPromotionalDiscounts(() => {
                     return {
                        ...state.promotionalDiscounts,
                        [discount_key]: committedValue > 0 ? true : false,
                        numberDiscountValues: {
                           ...state.promotionalDiscounts[
                              'numberDiscountValues'
                           ],
                           [discount_key]: committedValue,
                        },
                     }
                  })
               }}
               onBlur={(e) => {
                  // Get the input value as a number
                  let inputValue = Number(e.target.value)

                  // Enforce max value for percent discounts
                  if (
                     state.discount_list[discount_key]['DiscountType'] ===
                     'percent'
                  ) {
                     const maxValue = state.discount_list[discount_key][
                        'MaxDiscount'
                     ]
                        ? state.discount_list[discount_key]['MaxDiscount']
                        : 100
                     inputValue = Math.min(inputValue, maxValue)
                  }

                  // Round number to appropriate decimal places
                  const roundedNumber = isNaN(inputValue)
                     ? 0
                     : state.discount_list[discount_key]['DiscountType'] ==
                         'percent'
                       ? Number(inputValue.toFixed(5))
                       : Number(inputValue.toFixed(2))

                  // Strip extraneous trailing zeros for display (e.g. 1.50 → "1.5", 1.00 → "1")
                  setDisplayValue(
                     roundedNumber > 0 ? String(roundedNumber) : '',
                  )

                  // Update state with validated and formatted value
                  state.setPromotionalDiscounts(() => {
                     return {
                        ...state.promotionalDiscounts,
                        [discount_key]: roundedNumber > 0 ? true : false,
                        numberDiscountValues: {
                           ...state.promotionalDiscounts[
                              'numberDiscountValues'
                           ],
                           [discount_key]: roundedNumber,
                        },
                     }
                  })
               }}
            />
         </div>
      </div>
   )
}
